export interface ShareInput {
  title: string;
  url: string;
  text?: string;
}

export type ShareResult =
  | { kind: 'shared' }
  | { kind: 'copied' }
  | { kind: 'cancelled' }
  | { kind: 'error'; message: string };

export async function shareOrCopy(input: ShareInput): Promise<ShareResult> {
  if (typeof navigator !== 'undefined' && 'share' in navigator) {
    try {
      await (navigator as Navigator & { share: (i: ShareInput) => Promise<void> }).share(input);
      return { kind: 'shared' };
    } catch (err: unknown) {
      const name = (err as DOMException | undefined)?.name ?? '';
      if (name === 'AbortError') return { kind: 'cancelled' };
      // Fall through to clipboard if share fails for non-cancellation reasons.
    }
  }
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(input.url);
      return { kind: 'copied' };
    }
  } catch (err) {
    return {
      kind: 'error',
      message: err instanceof Error ? err.message : 'Clipboard unavailable',
    };
  }
  return { kind: 'error', message: 'Sharing unsupported in this browser' };
}

export interface ShareMediaInput extends ShareInput {
  /** Direct URL to the media file (S3 / CloudFront). We fetch it into a
   *  Blob so the OS share sheet sees an attachable image/video file. */
  mediaUrl: string;
  /** Desired filename in the share sheet, e.g. `photobooth-XYZ.jpg`. */
  filename: string;
  /** Content type — `image/jpeg`, `video/mp4`, `video/webm`. */
  contentType: string;
}

/**
 * Open the OS share sheet (or browser fallback) with the actual media
 * attached as a file. On iOS/Android this surfaces "Instagram", "Stories",
 * "X", "Messages", "AirDrop", etc.
 *
 * Falls back to `shareOrCopy` (URL-only share, then clipboard) when the
 * browser doesn't support file-shares — older Firefoxes, most desktop
 * Chromes, etc.
 */
export async function shareMedia(input: ShareMediaInput): Promise<ShareResult> {
  if (typeof navigator === 'undefined') {
    return { kind: 'error', message: 'No navigator' };
  }
  const nav = navigator as Navigator & {
    share?: (data: { files?: File[]; title?: string; text?: string; url?: string }) => Promise<void>;
    canShare?: (data: { files?: File[]; title?: string; text?: string; url?: string }) => boolean;
  };

  if (nav.share && nav.canShare) {
    try {
      const res = await fetch(input.mediaUrl, { mode: 'cors' });
      if (res.ok) {
        const blob = await res.blob();
        const file = new File([blob], input.filename, {
          type: input.contentType,
        });
        const payload = {
          files: [file],
          title: input.title,
          text: input.text ?? input.title,
          url: input.url,
        };
        if (nav.canShare(payload)) {
          try {
            await nav.share(payload);
            return { kind: 'shared' };
          } catch (err) {
            const name = (err as DOMException | undefined)?.name ?? '';
            if (name === 'AbortError') return { kind: 'cancelled' };
            // Fall through to URL-only share below.
          }
        }
      }
    } catch {
      // Network/decoding error — fall through to URL share.
    }
  }
  return shareOrCopy(input);
}
